import type { ChatThinkingStatus, ChatThinkingStep } from '../models/chat.model';

/** Trilha de raciocínio reidratada do histórico (passos + narrativa). */
export interface ReasoningTrail {
  reasoning: string;
  steps: ChatThinkingStep[];
}

/** Shape bruto (não confiável) de um passo persistido em `metadata.reasoningTrail`. */
interface RawReasoningStep {
  depth?: unknown;
  detail?: unknown;
  id?: unknown;
  status?: unknown;
  title?: unknown;
}

/** Shape bruto (não confiável) da trilha persistida em `metadata.reasoningTrail`. */
interface RawReasoningTrail {
  reasoning?: unknown;
  steps?: unknown;
}

/**
 * Reidrata a trilha de raciocínio persistida em `metadata.reasoningTrail` (gravada
 * pela API no `done`) para o histórico. Faz narrowing defensivo — JSON inesperado
 * vira `null` e o turno renderiza sem trilha. Mapeia `detail` → `output` para o
 * shape do `ChatThinkingStep`.
 */
export function readReasoningTrail(
  metadata: Record<string, unknown> | null | undefined,
): ReasoningTrail | null {
  const trail = (metadata as { reasoningTrail?: unknown } | null | undefined)?.reasoningTrail;
  if (!trail || typeof trail !== 'object') return null;

  const raw = trail as RawReasoningTrail;
  const steps = Array.isArray(raw.steps)
    ? raw.steps.flatMap((entry): ChatThinkingStep[] => {
        if (!entry || typeof entry !== 'object') return [];
        const step = entry as RawReasoningStep;
        if (typeof step.id !== 'string' || typeof step.title !== 'string') return [];
        const status: ChatThinkingStatus =
          step.status === 'done' || step.status === 'failed' ? step.status : 'running';
        return [
          {
            id: step.id,
            status,
            title: step.title,
            ...(typeof step.detail === 'string' ? { output: step.detail } : {}),
            ...(typeof step.depth === 'number' ? { depth: step.depth } : {}),
          },
        ];
      })
    : [];
  const reasoning = typeof raw.reasoning === 'string' ? raw.reasoning : '';

  return steps.length || reasoning ? { reasoning, steps } : null;
}
